/** libs */
import Image from 'next/legacy/image';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { ReactSVG } from 'react-svg';
import { LegacyRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import parse, { Element, HTMLReactParserOptions } from 'html-react-parser';
import { format } from 'date-fns';

/** components */
import Breadcrumbs from '../../shared/miscellaneous/breadcrumbs/breadcrumbs';
import TableNavbar from '@components/blog/table-navbar/table-navbar';
import GetInTouchComponent from '../../shared/interactions/get-in-touch/get-in-touch';
import FormIncut from '@components/shared/miscellaneous/incuts-types/form-incut/form-incut';
import LinkIncut from '@components/shared/miscellaneous/incuts-types/incut-link/incut-link';
import ArticlePreviewMedium from '../article-previews/article-preview-medium/article-preview-medium';

/** utils and models */
import { IArticle, IArticleRating } from '@models/article.interface';
import {
  articleAuthor,
  getImagesSourceFromHTML,
  imageSrcReplacer,
  isInternalArticleLink,
} from '@libs/utils/article.utils';
import {
  IArticleRatingStructureData,
  IArticleStructureData,
} from '@components/blog/article-page/article-page.data';

/** configs */
import { config } from '../../../config';

/** styles */
import style from './article-page.module.scss';
import { replaceDeprecatedIncuts, updateIncutsImage } from '@libs/utils/incut.utils';
import FAQSection from '@components/shared/blocks/faq';
import { IFAQItem } from '@components/shared/blocks/faq/types';
import ShouldRender from '@components/shared/boundaries/should-render';
import ReadingTimeBlock from '../reading-time-block/reading-time';
import Link from 'next/link';
import { restoreScrollBehavior, setScrollBehaviorAuto } from '@helpers/smooth-scroll.helper';
import { useTranslations } from 'next-intl';

const ShareButtons = dynamic(
  () => import('../../shared/miscellaneous/share-buttons/share-buttons'),
);

interface ArticlePageProps {
  article: IArticle;
  articleRating: IArticleRating;
}

interface IRatingResponse {
  user: {
    rating: number;
  };
  article: {
    count: number;
    average: string;
  };
}

const ArticlePage = ({ article, articleRating }: ArticlePageProps) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [ratingCount, setRatingCount] = useState(articleRating.count);
  const [averageRating, setAverageRating] = useState(articleRating.average);
  const relatedArticles = article.relatedArticles.slice(0, 3).map((ra: any) => ra.childArticle);
  const [titles, setTitles] = useState<string[]>([]);
  const [selectedTitle, setSelectedTitle] = useState<string>('');

  const { asPath } = useRouter();
  const titlesObserver = useRef<IntersectionObserver>();
  const faqTitleRef = useRef<HTMLHeadingElement>();

  const author = articleAuthor(article);
  const [firstName, lastName] = author.name.split(' ');
  const authorAbbreviation = `${firstName.charAt(0)}. ${lastName || ''}`;

  const content = imageSrcReplacer(article.content);

  const scrollbarRef = useRef<HTMLDivElement | null>(null);
  const scrollContent = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const MAX_WIDTH_ELEMENT = 100;
  const CLASS_INCUT_ITEM = 'incut-item';
  const [isChapterScroll, setIsChapterScroll] = useState<boolean>(true);
  const selectedTitleSelector = '[data-selected-title="true"]';
  const t = useTranslations();

  useEffect(() => {
    document.body.classList.add('footer-padding-page');
    const calculateScrollbarWidth = () => {
      if (!scrollContent.current || !scrollbarRef.current) return;

      const scrollPosition = window.innerHeight - scrollContent.current.getBoundingClientRect().top;

      const totalHeight = scrollContent.current.scrollHeight - window.innerHeight;
      const width = (scrollPosition / totalHeight) * MAX_WIDTH_ELEMENT;

      scrollbarRef.current.style.width = `${width}%`;
    };

    window.addEventListener('scroll', calculateScrollbarWidth);

    return () => {
      window.removeEventListener('scroll', calculateScrollbarWidth);
      document.body.classList.remove('footer-padding-page');
    };
  }, []);

  useEffect(() => {
    setScrollBehaviorAuto();
    return () => {
      restoreScrollBehavior();
    };
  }, []);

  useEffect(() => {
    const handleRouteChange = () => {
      window.scroll({
        top: 0,
        left: 0,
      });
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element && domNode.attribs) {
        if (domNode.attribs.class === 'form-incut') {
          const title = domNode.attribs['data-title'];
          const text = domNode.attribs['data-text'];
          const buttonText = domNode.attribs['data-button-text'];
          return (
            <FormIncut title={title} text={text} buttonText={buttonText} articleId={article.id} />
          );
        }
        if (domNode.attribs.class === CLASS_INCUT_ITEM) {
          const link = domNode.attribs['data-link'];
          const text = domNode.attribs['data-text'];
          return <LinkIncut text={text} link={link} />;
        }
        if (domNode.attribs.href && isInternalArticleLink(domNode.attribs.href)) {
          delete domNode.attribs.rel;
          return domNode;
        }
      }
    },
  };
  const faq: IFAQItem[] = article.faq.map((item) => ({
    question: item.question,
    answers: [item.answer],
  }));

  useEffect(() => {
    setSelectedTitle(() => '');
    updateArticleTitles(article.content);
  }, [asPath]);

  useEffect(() => {
    const titles: HTMLHeadingElement[] = Array.from(document.querySelectorAll('h2'));
    let isFirstCall = true;
    if (isChapterScroll) {
      const handleObserver = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry: IntersectionObserverEntry) => {
          if (entry.isIntersecting) {
            const firstIntersectingEntry = entries.find((entry) => entry.isIntersecting);
            if (firstIntersectingEntry) {
              if (firstIntersectingEntry.target.textContent !== null) {
                setSelectedTitle(firstIntersectingEntry.target.textContent);
              }
            }
          }

          if (!entry.isIntersecting && !isFirstCall) {
            const selected = document.querySelector(selectedTitleSelector);
            const disappearedBelow: boolean = entry.target.getBoundingClientRect().top > 0;
            const stillActive: boolean = entry.target.textContent === selected?.textContent;

            if (disappearedBelow && stillActive) {
              const previousTitleIdx: number =
                titles.findIndex(({ innerText }) => innerText === selected!.textContent) - 1;

              previousTitleIdx >= 0 && setSelectedTitle(() => titles[previousTitleIdx].innerText);
            }
          }
        });
        if (isFirstCall) {
          isFirstCall = false;
        }
      };

      titlesObserver.current = new IntersectionObserver(handleObserver, {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        threshold: 1,
      });

      titles.forEach((elem) => titlesObserver.current?.observe(elem));

      return () => {
        titlesObserver.current?.disconnect();
      };
    }
  }, [asPath, isChapterScroll]);

  useLayoutEffect(() => {
    updateIncutsImage();
    replaceDeprecatedIncuts();
  }, []);

  function formatDate(date: Date): string {
    const dateFormat = 'MMMM dd, yyyy';
    return format(Date.parse(String(date)), dateFormat);
  }

  function updateArticleTitles(content: string): void {
    const html = document.createElement('div');
    html.innerHTML = content;
    const titles = Array.from(html.querySelectorAll('h2')).map((t) => t.innerText);
    faqTitleRef.current?.innerText && titles.push(faqTitleRef.current?.innerText);
    setTitles(() => titles);
    html.remove();
  }

  function scrollToChapter(title: string): void {
    setIsChapterScroll(false);
    const h2Tags = Array.from(document.querySelectorAll('h2'));
    const selectedTitle = h2Tags.find(({ innerText }) => innerText === title);
    if (selectedTitle) {
      const headerHeight = 80;
      const targetOffset = selectedTitle.getBoundingClientRect().top;
      const scrollToPosition = window.scrollY + targetOffset - headerHeight;

      const animateScroll = () => {
        const currentTime = Date.now();
        const elapsed = Math.min(1, (currentTime - startTime) / duration);
        const ease = easeInOutQuad(elapsed);
        scrollTo(startingY + distance * ease, () => {});
        if (currentTime < startTime + duration) {
          requestAnimationFrame(animateScroll);
        }
      };

      const duration = 10;
      const startTime = Date.now();
      const startingY = window.scrollY;
      const distance = scrollToPosition - startingY;

      requestAnimationFrame(animateScroll);
      setSelectedTitle(() => selectedTitle.innerText);
    }
  }

  const scrollTo = (offset: number, callback: { (): void }) => {
    const onScroll = () => {
      if (Math.abs(window.scrollY - offset) <= 10) {
        window.removeEventListener('scroll', onScroll);
        setIsChapterScroll(true);
        callback();
      }
    };

    window.addEventListener('scroll', onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: 'smooth',
    });
  };

  function easeInOutQuad(t: number): number {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  const articleRatingStructureData: IArticleRatingStructureData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWorkSeries',
    name: article.header,
    aggregateRating: {
      '@type': 'AggregateRating',
      worstRating: articleRating.minRating,
      ratingValue: Number(articleRating.average),
      bestRating: articleRating.maxRating,
      reviewCount: Number(articleRating.count),
    },
  };

  const articleStructureData: IArticleStructureData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: `${config.HOST}${asPath}`,
    headline: article.header,
    datePublished: article.createdAt,
    dateModified: article.contentUpdatedAt,
    description: article.metaDescription,
    author: {
      '@type': 'Person',
      name: author.name,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Gofinans',
      logo: {
        '@type': 'ImageObject',
        url: 'https://gofinans.com/_next/image?url=/assets/header/logo/gofinans-logo.svg&w=96&q=75',
        width: 88,
        height: 24,
      },
    },
    image: getImagesSourceFromHTML(article.content),
  };

  return (
    <>
      <div ref={scrollbarRef} className={style.scroll__bar}></div>
      {[articleRatingStructureData, articleStructureData].map((structureData) => (
        <script
          key={structureData['@type']}
          type={'application/ld+json'}
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structureData) }}
        />
      ))}
      <div className={style.container}>
        <div className={style.article__header}>
          <Breadcrumbs lastElementTitle={article.header} />
          <div className={style.article__metadata}>
            <div className={style.article__metadata__info}>
              <h1 className={style.article__title}>{article.header}</h1>
              <ReadingTimeBlock readingTime={article.readingTime} articleLabel={article.label} />
              {/* TODO: temporary hidden author link */}
              {/* <Link scroll={false} href={`/blog/authors/${author.slug}`}>
                <div className={style.article__author}>
                  <div className={style.article__author_image}>
                    <Image
                      src={author.avatarUrl}
                      alt={author.name}
                      layout={'fill'}
                      objectFit={'contain'}
                    />
                  </div>
                  <div className={style.article__author__info}>
                    <span className={style.article__author__name}>
                      {t('Entity.By')} {authorAbbreviation}, {author.position && `${author.position},`} {t('Blog.Author.Company_use')}
                    </span>
                    <div className={style.article__timestamps}>
                      <div className={style.article__updated}>
                        <span>{formatDate(article.date)}</span>
                        <div className={style.divider__vertical}></div>
                        <span>Updated: {formatDate(article.contentUpdatedAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link> */}
            </div>
            <div className={style.article__metadata__wrap}>
              <Image
                className={style.article__metadata__wrap__img}
                sizes="1024px"
                layout={'fill'}
                src={article.posterUrl}
                alt={article.header}
              />
            </div>
          </div>
        </div>
        <section className={style.article}>
          <div className={`${style.article__share} ${style.desktop}`}>
            <ShareButtons />
          </div>
          <div ref={scrollContent} className={style.article__content}>
            <div className={style.article__main}>{parse(content, options)}</div>
            <ShouldRender if={faq.length}>
              <FAQSection
                titleRef={faqTitleRef as LegacyRef<HTMLHeadingElement>}
                title="FAQ"
                itemsFAQ={faq}
                hoverColor="grey"
              />
            </ShouldRender>
          </div>

          <div className={`${style.article__navigation} ${style.desktop}`}>
            <span className={style.article__navigation_title}>Table of contents</span>
            {titles.length ? (
              <TableNavbar titles={titles} activeTitle={selectedTitle} handler={scrollToChapter} />
            ) : (
              ''
            )}
          </div>
        </section>
        <section className={style.form}>
          <div className={style.form__incut}>
            <GetInTouchComponent withSubtitle={false} />
          </div>
        </section>
        <section className={style.recommendations}>
          <span className={style.recommendations__title}>Related articles</span>

          <div className={style.recommendations__items}>
            {relatedArticles.map((item) => {
              return <ArticlePreviewMedium key={item.id} article={item} />;
            })}
          </div>
          <div className={style.recommendations__items__for__tablet}>
            {relatedArticles.slice(0, 2).map((item) => {
              return <ArticlePreviewMedium key={item.id} article={item} />;
            })}
          </div>
        </section>
        <div className={`${style.article__share_mobile}`}>
          <ShareButtons />
        </div>
      </div>
    </>
  );
};

export default ArticlePage;
